/**
 * Button 컴포넌트
 * 재사용 가능한 버튼 컴포넌트 - 프로젝트 전체에서 사용
 */

import { forwardRef } from 'react';
import { combineClasses } from '@/shared/lib/classNames';
import {
  CloseIcon,
  DeleteIcon,
  EditIcon,
  NewUploadIcon,
  ProjectDetailEditIcon,
} from '../Icons/Icons';
import styles from './Button.module.css';
import { type ButtonProps, type ButtonVariant } from './Button.types';

/**
 * 액션 variant에 따른 기본 아이콘 반환
 * 아이콘 크기는 버튼 크기에 따라 자동 조정
 */
const getActionIcon = (variant: ButtonVariant, size: 'small' | 'medium' | 'large') => {
  const iconSize = size === 'small' ? 14 : size === 'medium' ? 16 : 18;

  switch (variant) {
    case 'save':
      return <ProjectDetailEditIcon size={iconSize} />;
    case 'cancel':
      return <CloseIcon size={iconSize} />;
    case 'upload':
      return <NewUploadIcon size={iconSize} />;
    case 'download':
      return <NewUploadIcon size={iconSize} />; // 동일 아이콘 사용
    case 'delete':
      return <DeleteIcon size={iconSize} />;
    case 'edit':
      return <EditIcon size={iconSize} />;
    default:
      return null;
  }
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'medium',
      loading = false,
      fullWidth = false,
      leftIcon,
      rightIcon,
      disabled,
      className,
      ...props
    },
    ref,
  ) => {
    // 버튼이 비활성화되는 조건
    const isDisabled = disabled || loading;

    // 액션 variant인 경우 자동으로 아이콘 제공 (수동 아이콘이 없을 때만)
    // leftIcon={null}이 명시적으로 전달되면 아이콘 숨김
    const actionIcon = getActionIcon(variant, size);
    const finalLeftIcon = leftIcon !== undefined ? leftIcon : actionIcon;

    // CSS 클래스를 동적으로 조합
    const buttonClasses = combineClasses(
      styles.button, // 기본 버튼 스타일
      styles[variant], // 변형 (primary, secondary 등)
      styles[size], // 크기 (small, medium, large)
      fullWidth && styles.fullWidth, // 전체 너비
      isDisabled && styles.disabled, // 비활성화
      loading && styles.loading, // 로딩
      className, // 외부에서 전달된 추가 클래스
    );

    return (
      <button ref={ref} className={buttonClasses} disabled={isDisabled} {...props}>
        {/* 왼쪽 아이콘 또는 로딩 스피너 */}
        {loading ? <span className={styles.spinner} data-testid="button-spinner" /> : finalLeftIcon}

        {/* 버튼 텍스트 */}
        {children && <span>{children}</span>}

        {/* 오른쪽 아이콘 (로딩 중일 때는 숨김) */}
        {!loading && rightIcon}
      </button>
    );
  },
);

// 컴포넌트 이름 설정 (개발 도구에서 보임)
Button.displayName = 'Button';
